import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "../../../../../../../services/queryEndPoint";
import { Api } from "../../../../../../../services/interceptor";
import type { IMatchDetails } from "../types/matchDetails.d";
import type {
  ICombinedPointsTableResponse,
  IDivisionSummary,
  IPointsRow,
} from "../../../../../types/home.d";
import { Images } from "../../../../../../../utils/imageConstants";

import { resolvePublicImageUrl } from "../../../../../../../helper/helper";

interface UpcomingMatchPointsTableTabProps {
  matchSummary?: IMatchDetails;
}

const UpcomingMatchPointsTableTab: React.FC<UpcomingMatchPointsTableTabProps> = ({ matchSummary }) => {
  const tournamentId = sessionStorage.getItem("tournamentId");

  const { data: divisionsPayload } = useQuery({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    enabled: !!tournamentId,
    queryFn: async () =>
      (await Api.get(`${QUERY_KEY.getDivisions}/${tournamentId}`)).data,
  });

  const divisions: IDivisionSummary[] = useMemo(() => {
    if (!divisionsPayload) return [];
    if (Array.isArray(divisionsPayload)) return divisionsPayload;
    const p = divisionsPayload as Record<string, unknown>;
    return ((p.divisionResponseList ?? p.divisionResponses ?? []) as IDivisionSummary[]);
  }, [divisionsPayload]);

  const firstDivisionId = divisions[0]?.divisionId;

  const { data: tableData } = useQuery<ICombinedPointsTableResponse>({
    queryKey: [QUERY_KEY.getCombinedPointsTableByDivision, firstDivisionId],
    enabled: !!firstDivisionId,
    queryFn: async () =>
      (await Api.get<ICombinedPointsTableResponse>(
        `${QUERY_KEY.getCombinedPointsTableByDivision}/${firstDivisionId}`
      )).data,
  });

  const allRows: IPointsRow[] = useMemo(() => {
    if (!tableData) return [];
    return Object.values(tableData).flat();
  }, [tableData]);

  const teamOneName = matchSummary?.teamOne?.teamName ?? "";
  const teamTwoName = matchSummary?.teamTwo?.teamName ?? "";

  const displayRows = useMemo(() => {
    if (!allRows.length) return [];
    const matched = allRows.filter(
      (row) =>
        row.teamSnapshotName?.toLowerCase().includes(teamOneName.toLowerCase()) ||
        row.teamSnapshotName?.toLowerCase().includes(teamTwoName.toLowerCase())
    );
    const rows = matched.length ? matched : allRows.slice(0, 5);
    return [...rows].sort((a, b) => a.rank - b.rank);
  }, [allRows, teamOneName, teamTwoName]);

  return (
    <div className="upcoming-points-table-tab-container">
      <div className="upcoming-points-table-tab-header">
        <p>Points Table</p>
      </div>
      <div className="upcoming-points-table-tab-content">
        <table className="upcoming-points-table">
          <thead>
            <tr>
              <th className="upcoming-points-table-th teams-col">TEAMS</th>
              <th className="upcoming-points-table-th stat-col">M</th>
              <th className="upcoming-points-table-th stat-col">W</th>
              <th className="upcoming-points-table-th stat-col">L</th>
              <th className="upcoming-points-table-th stat-col">N/R</th>
              <th className="upcoming-points-table-th stat-col">Pt.</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((team) => (
              <tr key={team.id} className="upcoming-points-table-tr">
                <td className="upcoming-points-table-td teams-col">
                  <div className="upcoming-points-table-team-cell">
                    <ol start={team.rank} className="upcoming-points-table-rank">
                      <li>
                        <span className="upcoming-points-table-rank-text">&nbsp;</span>
                      </li>
                    </ol>
                    <div className="upcoming-points-table-team-info">
                      <p className="upcoming-points-table-team-name">{team.teamSnapshotName}</p>
                    </div>
                  </div>
                </td>
                <td className="upcoming-points-table-td stat-col"><p>{team.matches}</p></td>
                <td className="upcoming-points-table-td stat-col"><p>{team.won}</p></td>
                <td className="upcoming-points-table-td stat-col"><p>{team.lost}</p></td>
                <td className="upcoming-points-table-td stat-col"><p>{team.noResult}</p></td>
                <td className="upcoming-points-table-td stat-col"><p>{team.points}</p></td>
              </tr>
            ))}
            {!displayRows.length && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 16 }}>
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UpcomingMatchPointsTableTab;
